import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Globe, Plus, Calendar, Edit, Trash2, Search, AlertTriangle, 
  Loader2, Shield, ExternalLink, Server, User, Clock, RefreshCw,
  CheckCircle2, XCircle, DollarSign, Link2
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const REGISTRARS = ['Planethoster', 'OVH', 'Gandi', 'Namecheap', 'GoDaddy', 'Ionos', 'Google Domains', 'Cloudflare', 'Autre'];
const HOSTING_PROVIDERS = ['Planethoster', 'OVH', 'O2Switch', 'Infomaniak', 'AWS', 'DigitalOcean', 'Vercel', 'Netlify', 'Autre'];

const DomainsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Data
  const [domains, setDomains] = useState([]);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form fields
  const [domainName, setDomainName] = useState('');
  const [clientId, setClientId] = useState('');
  const [registrar, setRegistrar] = useState('');
  const [hostingProvider, setHostingProvider] = useState('');
  const [creationDate, setCreationDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [autoRenew, setAutoRenew] = useState(false);
  const [sslEnabled, setSslEnabled] = useState(false);
  const [sslExpiryDate, setSslExpiryDate] = useState('');
  const [dnsProvider, setDnsProvider] = useState('');
  const [linkedToSite, setLinkedToSite] = useState(false);
  const [siteUrl, setSiteUrl] = useState('');
  const [billingClient, setBillingClient] = useState(true);
  const [annualCost, setAnnualCost] = useState('');
  const [notes, setNotes] = useState('');

  const isAgency = ['agency', 'project_manager', 'customer_service'].includes(user?.role);

  useEffect(() => {
    fetchDomains();
    if (isAgency) fetchClients();
  }, []);

  const fetchDomains = async () => {
    try {
      const response = await api.get('/domains/');
      setDomains(response.data || []);
    } catch (error) {
      console.error('Error fetching domains:', error);
      toast.error('Erreur lors du chargement des domaines');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/users/', { params: { role: 'client' } });
      setClients(response.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const openModal = (domain = null) => {
    if (domain) {
      setEditingDomain(domain);
      setDomainName(domain.domain_name || '');
      setClientId(domain.client_id || '');
      setRegistrar(domain.registrar || '');
      setHostingProvider(domain.hosting_provider || '');
      setCreationDate(domain.creation_date ? domain.creation_date.split('T')[0] : '');
      setExpiryDate(domain.expiry_date ? domain.expiry_date.split('T')[0] : '');
      setAutoRenew(domain.auto_renew || false);
      setSslEnabled(domain.ssl_enabled || false);
      setSslExpiryDate(domain.ssl_expiry_date ? domain.ssl_expiry_date.split('T')[0] : '');
      setDnsProvider(domain.dns_provider || '');
      setLinkedToSite(domain.linked_to_site || false);
      setSiteUrl(domain.site_url || '');
      setBillingClient(domain.billing_client !== false);
      setAnnualCost(domain.annual_cost?.toString() || '');
      setNotes(domain.notes || '');
    } else {
      setEditingDomain(null);
      setDomainName('');
      setClientId('');
      setRegistrar('');
      setHostingProvider('');
      setCreationDate('');
      setExpiryDate('');
      setAutoRenew(false);
      setSslEnabled(false);
      setSslExpiryDate('');
      setDnsProvider('');
      setLinkedToSite(false);
      setSiteUrl('');
      setBillingClient(true);
      setAnnualCost('');
      setNotes('');
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!domainName || !clientId) {
      toast.error('Nom de domaine et client sont obligatoires');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        domain_name: domainName,
        client_id: clientId,
        registrar,
        hosting_provider: hostingProvider || null,
        creation_date: creationDate ? new Date(creationDate).toISOString() : null,
        expiry_date: expiryDate ? new Date(expiryDate).toISOString() : null,
        auto_renew: autoRenew,
        ssl_enabled: sslEnabled,
        ssl_expiry_date: sslExpiryDate ? new Date(sslExpiryDate).toISOString() : null,
        dns_provider: dnsProvider || null,
        linked_to_site: linkedToSite,
        site_url: siteUrl || null,
        billing_client: billingClient,
        annual_cost: annualCost ? parseFloat(annualCost) : null,
        notes: notes || null
      };
      
      if (editingDomain) {
        await api.put(`/domains/${editingDomain.id}`, payload);
        toast.success('Domaine mis à jour');
      } else {
        await api.post('/domains/', payload);
        toast.success('Domaine créé');
      }
      
      setShowModal(false);
      fetchDomains();
    } catch (error) {
      console.error('Error saving domain:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (domainId) => {
    if (!window.confirm('Supprimer ce domaine ?')) return;
    
    try {
      await api.delete(`/domains/${domainId}`);
      toast.success('Domaine supprimé');
      fetchDomains();
    } catch (error) {
      console.error('Error deleting domain:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (status, daysLeft) => {
    if (status === 'expired') {
      return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Expiré</Badge>;
    }
    if (status === 'expiring_soon') {
      return <Badge className="bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3 mr-1" />{daysLeft}j restants</Badge>;
    }
    return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Actif</Badge>;
  };

  // Filter domains
  const filteredDomains = domains.filter(domain => {
    const matchesSearch = domain.domain_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          domain.client_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || domain.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    total: domains.length,
    expiringSoon: domains.filter(d => d.status === 'expiring_soon').length,
    expired: domains.filter(d => d.status === 'expired').length,
    withSsl: domains.filter(d => d.ssl_enabled).length
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      <div className={cn("transition-all duration-300", collapsed ? "md:ml-20" : "md:ml-64")}>
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        
        <main className="p-4 md:p-8 pt-40 md:pt-36">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                Gestion des Domaines
              </h1>
              <p className="text-slate-500 mt-1">Gérez les noms de domaine de vos clients</p>
            </div>
            {isAgency && (
              <Button onClick={() => openModal()} style={{ backgroundColor: theme.colors?.accent }} data-testid="new-domain-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau domaine
              </Button>
            )}
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total</p>
                    <p className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>{stats.total}</p>
                  </div>
                  <Globe className="w-8 h-8 text-slate-300" />
                </div>
              </CardContent>
            </Card>
            <Card className={stats.expiringSoon > 0 ? "border-amber-200 bg-amber-50" : ""}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Expire bientôt</p>
                    <p className="text-2xl font-bold text-amber-600">{stats.expiringSoon}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-amber-300" />
                </div>
              </CardContent>
            </Card>
            <Card className={stats.expired > 0 ? "border-red-200 bg-red-50" : ""}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Expirés</p>
                    <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
                  </div>
                  <XCircle className="w-8 h-8 text-red-300" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Avec SSL</p>
                    <p className="text-2xl font-bold text-emerald-600">{stats.withSsl}</p>
                  </div>
                  <Shield className="w-8 h-8 text-emerald-300" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Rechercher un domaine ou client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="expiring_soon">Expire bientôt</SelectItem>
                <SelectItem value="expired">Expirés</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Domains Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredDomains.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Globe className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucun domaine</h3>
                <p className="text-slate-500">
                  {searchTerm ? 'Aucun résultat pour votre recherche' : 'Commencez par ajouter un domaine'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Domaine</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Registrar</TableHead>
                    <TableHead>Expiration</TableHead>
                    <TableHead>SSL</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDomains.map((domain) => (
                    <TableRow key={domain.id} className={cn(
                      domain.status === 'expired' && "bg-red-50",
                      domain.status === 'expiring_soon' && "bg-amber-50"
                    )}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-slate-400" />
                          <div>
                            <span className="font-medium">{domain.domain_name}</span>
                            {domain.linked_to_site && (
                              <div className="flex items-center gap-1 text-xs text-slate-500">
                                <Link2 className="w-3 h-3" />
                                Site lié
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{domain.client_name || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">{domain.registrar || '-'}</span>
                      </TableCell>
                      <TableCell>
                        {domain.expiry_date ? (
                          <div className="flex items-center gap-1 text-sm">
                            <Calendar className="w-3 h-3 text-slate-400" />
                            {new Date(domain.expiry_date).toLocaleDateString('fr-FR')}
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {domain.ssl_enabled ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <Shield className="w-3 h-3 mr-1" />SSL
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-400">Non</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(domain.status, domain.days_until_expiry)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {domain.site_url && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => window.open(domain.site_url, '_blank')}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          )}
                          {isAgency && (
                            <>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => openModal(domain)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-red-500"
                                onClick={() => handleDelete(domain.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </main>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              {editingDomain ? 'Modifier le domaine' : 'Nouveau domaine'}
            </DialogTitle>
            <DialogDescription>
              Renseignez les informations du nom de domaine
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom de domaine *</Label>
                <Input 
                  value={domainName}
                  onChange={(e) => setDomainName(e.target.value)}
                  placeholder="exemple.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select value={clientId} onValueChange={setClientId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.company_name || `${client.first_name} ${client.last_name}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Registrar & Hosting */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Registrar</Label>
                <Select value={registrar} onValueChange={setRegistrar}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {REGISTRARS.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Hébergeur</Label>
                <Select value={hostingProvider} onValueChange={setHostingProvider}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {HOSTING_PROVIDERS.map((h) => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Date de création</Label>
                <Input 
                  type="date"
                  value={creationDate}
                  onChange={(e) => setCreationDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Date d'expiration</Label>
                <Input 
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>DNS Provider</Label>
                <Input 
                  value={dnsProvider}
                  onChange={(e) => setDnsProvider(e.target.value)}
                  placeholder="Cloudflare, OVH..."
                />
              </div>
            </div>

            {/* SSL Section */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-emerald-500" />
                  <Label>Certificat SSL actif</Label>
                </div>
                <Switch checked={sslEnabled} onCheckedChange={setSslEnabled} />
              </div>
              {sslEnabled && (
                <div className="space-y-2">
                  <Label>Date d'expiration SSL</Label>
                  <Input 
                    type="date"
                    value={sslExpiryDate}
                    onChange={(e) => setSslExpiryDate(e.target.value)}
                  />
                </div>
              )}
            </div>

            {/* Site Link */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-blue-500" />
                  <Label>Rattaché à un site</Label>
                </div>
                <Switch checked={linkedToSite} onCheckedChange={setLinkedToSite} />
              </div>
              {linkedToSite && (
                <div className="space-y-2">
                  <Label>URL du site</Label>
                  <Input 
                    value={siteUrl}
                    onChange={(e) => setSiteUrl(e.target.value)}
                    placeholder="https://exemple.com"
                  />
                </div>
              )}
            </div>

            {/* Billing */}
            <div className="border-t pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-slate-500" />
                    <Label>Facturation client</Label>
                  </div>
                  <Switch checked={billingClient} onCheckedChange={setBillingClient} />
                </div>
                <div className="space-y-2">
                  <Label>Coût annuel (€)</Label>
                  <Input 
                    type="number"
                    value={annualCost}
                    onChange={(e) => setAnnualCost(e.target.value)}
                    placeholder="15.00"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-slate-500" />
                  <Label>Renouvellement automatique</Label>
                </div>
                <Switch checked={autoRenew} onCheckedChange={setAutoRenew} />
              </div>
            </div>

            {/* Notes */}
            <div className="border-t pt-4">
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Informations complémentaires..."
                  rows={3}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving} style={{ backgroundColor: theme.colors?.accent }}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editingDomain ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DomainsPage;
